import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, PartyPopper, Wallet, AlertTriangle, Mail, History, Link2,
  MapPin, MessageCircle, MessagesSquare, Monitor, RefreshCw, UploadCloud, Settings, Droplet, UsersRound, ShieldCheck,
  CircleHelp, FileText, Info, Shield,
} from "lucide-react";
import { useUpload } from "../../context/UploadContext";
import { useAutoUpload } from "../../context/AutoUploadContext";
import { useChat } from "../../context/ChatContext";
import { useAuth } from "../../context/AuthContext";
import WatermarkSettingsModal from "../common/WatermarkSettingsModal";
import { checkCompanion, _resetCompanionStatus } from "../../services/videoCompressionService";
import { getWebsiteLinks } from "../../services/websiteLinkService";
import { color, radius, shadow, transition } from "../../styles/theme";

const NAV_ITEMS = [
  { key: "dashboard", path: "/dashboard", icon: LayoutDashboard },
  { key: "events", path: "/events", icon: PartyPopper },
  { key: "wallet", path: "/wallet", icon: Wallet },
  { key: "disputes", path: "/disputes", icon: AlertTriangle },
  { key: "accessRequests", path: "/access-requests", icon: Mail },
  { key: "eventHistory", path: "/history", icon: History },
  { key: "socialLinks", path: "/rooms/social-links", icon: Link2 },
  { key: "locations", path: "/locations", icon: MapPin },
  { key: "chat", path: "/chat", icon: MessagesSquare },
  { key: "room", path: "/rooms", icon: MessageCircle },
  { key: "roomAccess", path: "/rooms/access-requests", icon: Mail },
  { key: "team", path: "/team", icon: UsersRound },
  { key: "subAccounts", path: "/sub-accounts", icon: ShieldCheck },
  { key: "linkedDevices", path: "/linked-devices", icon: Link2 },
  { key: "studioApp", path: "/companion", icon: Monitor },
];

const Sidebar = ({ isCompact = false, collapsed = false, open = false, onClose }) => {
  const { t, i18n } = useTranslation("layout");
  const { hasActiveUploads, activeSessionCount, sessions } = useUpload();
  const { activeJobCount } = useAutoUpload();
  const chat = useChat();
  const { isSubAccount, subAccountPermissions } = useAuth();
  const hasAnySessions = Object.keys(sessions).length > 0;

  // Sub-Account permission gating (spec ch.11.2) - mirrors the page-level
  // requiredPermission gates in AppRoutes.jsx, so a Sub-Account never sees a
  // nav link to a page it would immediately hit an Access Denied screen on.
  // Cross-functional pages accept any relevant block so, for example, a
  // Gallery-only Sub-Account can reach an Event without receiving Event-edit rights.
  const NAV_PERMISSION_MAP = {
    events: ["CanManageEvents", "CanManageAccessQrCheckIn", "CanManageGallery", "CanManageSingleMedia", "CanManagePricing", "CanManageSupport"],
    room: ["CanManageRooms", "CanManageGallery", "CanManageSingleMedia", "CanManagePricing"],
    eventHistory: ["CanManageEvents", "CanManageGallery", "CanManageSingleMedia"],
    locations: ["CanManageAccessQrCheckIn"],
    subAccounts: ["CanManageSubAccounts"],
    wallet: ["CanManageWallet"],
    disputes: ["CanManageSupport"],
    accessRequests: ["CanManageAccessQrCheckIn"],
    roomAccess: ["CanManageAccessQrCheckIn"],
    socialLinks: ["CanManageRooms"],
    team: ["CanManageEvents"],
    linkedDevices: ["CanManageAccessQrCheckIn"],
  };
  const hasAnyPermission = (permissions) => !isSubAccount || permissions.some((permission) => subAccountPermissions?.[permission]);
  const visibleNavItems = NAV_ITEMS.filter((item) => {
    const requiredPermissions = NAV_PERMISSION_MAP[item.key];
    return !requiredPermissions || hasAnyPermission(requiredPermissions);
  });
  const [showWatermark, setShowWatermark] = useState(false);
  const [studioOnline, setStudioOnline] = useState(null); // null = unknown
  const [websiteLinks, setWebsiteLinks] = useState({});

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      _resetCompanionStatus();
      const ok = await checkCompanion();
      if (!cancelled) setStudioOnline(ok);
    };
    poll();
    const id = setInterval(poll, 10_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    let active = true;
    getWebsiteLinks()
      .then((res) => {
        if (active) setWebsiteLinks(res.data?.Result || res.data || {});
      })
      // External-information links simply remain hidden until the catalogue is
      // available; no hard-coded URLs can become stale or use the wrong locale.
      .catch(() => { if (active) setWebsiteLinks({}); });
    return () => { active = false; };
  }, []);

  const websiteLanguage = i18n.language?.slice(0, 2).toUpperCase() === "DE" ? "DE" : "EN";
  const websiteLinkItems = [
    { type: "FAQ", label: t("sidebar.faq"), icon: CircleHelp },
    { type: "ABOUT_US", label: t("sidebar.aboutUs"), icon: Info },
    { type: "PRIVACY_POLICY", label: t("sidebar.privacyPolicy"), icon: Shield },
    { type: "PROMOTER", label: t("sidebar.promoterTerms"), icon: FileText },
  ].map((item) => ({ ...item, url: websiteLinks?.[item.type]?.[websiteLanguage] })).filter((item) => item.url);

  const linkStyle = ({ isActive }) => ({ ...s.link, ...(isActive ? s.activeLink : {}) });

  return (
    <aside
      style={{
        ...s.sidebar,
        ...(!isCompact && collapsed ? s.sidebarCollapsed : {}),
        ...(isCompact
          ? { ...s.sidebarCompact, transform: open ? "translateX(0)" : "translateX(-100%)" }
          : {}),
      }}
    >
      <div style={s.navSection}>
        {isCompact && (
          <button type="button" className="btn btn-ghost btn-sm" style={s.closeBtn} onClick={onClose} aria-label={t("sidebar.close")}>
            {t("sidebar.close")} ✕
          </button>
        )}
        {!collapsed && <span style={s.sectionLabel}>{t("sidebar.menu")}</span>}
        {visibleNavItems.map((item) => (
          <NavLink key={item.path} className="sidebar-link" to={item.path} end={item.path === "/rooms"} title={collapsed ? t(`sidebar.${item.key}`) : undefined} style={({ isActive }) => ({ ...linkStyle({ isActive }), ...(collapsed ? s.collapsedLink : {}) })}>
            <item.icon size={16} strokeWidth={2} style={s.icon} />
            {!collapsed && <span>{t(`sidebar.${item.key}`)}</span>}
            {item.path === "/companion" && (
              <span
                title={studioOnline === null ? t("sidebar.studioChecking") : studioOnline ? t("sidebar.studioOnline") : t("sidebar.studioOffline")}
                style={{ ...s.statusDot, background: studioOnline === null ? color.gray500 : studioOnline ? color.success500 : color.danger500 }}
              />
            )}
            {item.path === "/chat" && chat?.unreadTotal > 0 && <span style={s.uploadBadge}>{chat.unreadTotal}</span>}
          </NavLink>
        ))}
        {hasAnyPermission(["CanManageGallery"]) && (
          <NavLink className="sidebar-link" to="/auto-upload" title={collapsed ? t("sidebar.autoUpload") : undefined} style={({ isActive }) => ({ ...linkStyle({ isActive }), ...(collapsed ? s.collapsedLink : {}) })}>
            <RefreshCw size={16} strokeWidth={2} style={s.icon} />
            {!collapsed && <span>{t("sidebar.autoUpload")}</span>}
            {activeJobCount > 0 && <span style={s.uploadBadge}>{activeJobCount}</span>}
          </NavLink>
        )}
        {hasAnySessions && hasAnyPermission(["CanManageGallery"]) && (
          <NavLink className="sidebar-link" to="/uploads" title={collapsed ? t("sidebar.uploads") : undefined} style={({ isActive }) => ({ ...s.link, ...s.uploadLink, ...(isActive ? s.activeLink : {}), ...(collapsed ? s.collapsedLink : {}) })}>
            <UploadCloud size={16} strokeWidth={2} style={{ ...s.icon, ...(hasActiveUploads ? s.uploadPulse : undefined) }} />
            {!collapsed && <span>{t("sidebar.uploads")}</span>}
            {activeSessionCount > 0 && <span style={s.uploadBadge}>{activeSessionCount}</span>}
          </NavLink>
        )}

        {!collapsed && <span style={{ ...s.sectionLabel, marginTop: 14 }}>{t("sidebar.settings")}</span>}
        <NavLink className="sidebar-link" to="/settings" title={collapsed ? t("sidebar.accountSettings") : undefined} style={({ isActive }) => ({ ...linkStyle({ isActive }), ...(collapsed ? s.collapsedLink : {}) })}>
          <Settings size={16} strokeWidth={2} style={s.icon} />
          {!collapsed && <span>{t("sidebar.accountSettings")}</span>}
        </NavLink>
        {hasAnyPermission(["CanManageGallery"]) && (
          <button className="sidebar-link" style={{ ...s.linkAsButton, ...(collapsed ? s.collapsedLink : {}) }} onClick={() => setShowWatermark(true)} title={collapsed ? t("sidebar.watermark") : undefined}>
            <Droplet size={16} strokeWidth={2} style={s.icon} />
            {!collapsed && <span>{t("sidebar.watermark")}</span>}
          </button>
        )}
      </div>

      {websiteLinkItems.length > 0 && (
        <div style={s.websiteLinks}>
          {!collapsed && <span style={s.sectionLabel}>{t("sidebar.helpAndInformation")}</span>}
          {websiteLinkItems.map(({ type, label, icon: Icon, url }) => (
            <a
              key={type}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...s.websiteLink, ...(collapsed ? s.collapsedLink : {}) }}
              title={collapsed ? label : undefined}
              onClick={onClose}
            >
              <Icon size={14} strokeWidth={2} style={s.icon} />
              {!collapsed && <span>{label}</span>}
            </a>
          ))}
        </div>
      )}

      {showWatermark && (
        <WatermarkSettingsModal onClose={() => setShowWatermark(false)} onSaved={() => {}} />
      )}
    </aside>
  );
};

const s = {
  sidebar: {
    width: 232, height: "calc(100vh - 58px)", minHeight: 0, position: "sticky", top: 58,
    overflowY: "auto", background: color.gray900, paddingTop: 10,
    display: "flex", flexDirection: "column", flexShrink: 0,
    borderRight: "1px solid rgba(255,255,255,0.04)",
  },
  sidebarCollapsed: { width: 64 },
  // Below the tablet breakpoint the sidebar leaves normal document flow and
  // becomes an off-canvas drawer under the sticky Navbar (58px), sliding in
  // from the left; Layout.jsx owns the open/close state + backdrop.
  sidebarCompact: {
    position: "fixed", top: 58, left: 0, bottom: 0, zIndex: 150,
    minHeight: 0, height: "calc(100vh - 58px)", overflowY: "auto",
    width: "min(80vw, 300px)", transition: `transform ${transition}`,
    boxShadow: shadow.lg,
  },
  closeBtn: {
    alignSelf: "flex-end", margin: "0 10px 6px", color: "#9ca3af",
  },
  navSection: { display: "flex", flexDirection: "column", flex: 1, gap: 1, padding: "8px 10px" },
  sectionLabel: {
    fontSize: 10, fontWeight: 700, color: "#6b7280", letterSpacing: 1,
    padding: "10px 12px 8px", textTransform: "uppercase",
  },
  link: {
    display: "flex", alignItems: "center", gap: 11, padding: "9px 12px",
    color: "#9ca3af", textDecoration: "none", fontSize: 13.5, fontWeight: 500,
    borderRadius: radius.sm, transition: "background 150ms, color 150ms",
  },
  linkAsButton: {
    display: "flex", alignItems: "center", gap: 11, padding: "9px 12px", color: "#9ca3af",
    fontSize: 13.5, fontWeight: 500, background: "none", border: "none", cursor: "pointer",
    textAlign: "left", borderRadius: radius.sm, width: "100%",
  },
  collapsedLink: { justifyContent: "center", gap: 0, paddingLeft: 10, paddingRight: 10 },
  activeLink: { color: "#fff", background: "rgba(108,99,255,0.16)" },
  icon: { flexShrink: 0 },
  statusDot: { marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  uploadLink: { borderTop: "1px solid rgba(255,255,255,0.06)", marginTop: 5, paddingTop: 10 },
  uploadPulse: { animation: "pulse 1.5s infinite" },
  uploadBadge: {
    marginLeft: "auto", background: color.primary500, color: "#fff", fontSize: 10,
    fontWeight: 700, padding: "2px 7px", borderRadius: radius.pill, lineHeight: "14px",
  },
  websiteLinks: {
    display: "flex", flexDirection: "column", gap: 1, padding: "8px 10px 14px",
    borderTop: "1px solid rgba(255,255,255,0.08)",
  },
  websiteLink: {
    display: "flex", alignItems: "center", gap: 9, padding: "6px 12px", color: "#9ca3af",
    textDecoration: "none", fontSize: 11.5, fontWeight: 500,
  },
};

export default Sidebar;
